import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import ListingPage from './ListingPage';
import Icon from '../comps/Icon';
import reservationsSvc from '../services/reservations';
import manageareas from '../services/manageareas';
import managetables from '../services/managetables';
import businessConfigSvc from '../services/businessConfig';
import ReservationDrawer from '../comps/ReservationDrawer';
import { toHHMM, toMinutes, freeTablesFor } from '../services/seatingEngine';

// Base columns for the reservation list (also the basic-plan LIVE day view).
export const RESERVATION_COLUMNS = [
    { id: 'name', text: 'שם מלא' },
    { id: 'size', text: 'כמות אנשים' },
    { id: 'area', text: 'איזור' },
    { id: 'table', text: 'שולחן' },
    { id: 'time', text: 'שעה' },
    { id: 'phone', text: 'מספר פלאפון' },
];

const STATUS = {
    booked: { label: 'הוזמן', bg: 'var(--bg-subtle)', color: 'var(--text-muted)' },
    arrived: { label: 'הגיע', bg: 'var(--brand-soft)', color: 'var(--success)' },
    seated: { label: 'התיישב', bg: 'var(--brand)', color: 'var(--on-brand)' },
    'no-show': { label: 'לא הגיע', bg: 'var(--brand-soft)', color: 'var(--danger)' },
    cancelled: { label: 'בוטל', bg: 'var(--bg-subtle)', color: 'var(--text-faint)' },
};
const badgeStyle = (s) => ({
    display: 'inline-block', padding: '2px 10px', borderRadius: 'var(--r-pill)',
    fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-bold)',
    background: (STATUS[s] || STATUS.booked).bg, color: (STATUS[s] || STATUS.booked).color,
});
const actBtn = {
    border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)',
    borderRadius: 'var(--r-sm)', padding: '4px 8px', fontSize: 'var(--fs-xs)',
    fontFamily: 'var(--font)', cursor: 'pointer', minHeight: 30,
};
const field = {
    padding: '10px 12px', border: '1.5px solid var(--border)', borderRadius: 'var(--r-md)',
    background: 'var(--bg-subtle)', fontFamily: 'var(--font)', fontSize: 'var(--fs-sm)',
    color: 'var(--text)', minHeight: 44, boxSizing: 'border-box',
};
const todayISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const EMPTY = { guestName: '', guestPhone: '', startTime: '19:00', numberOfGuests: '2', area: '', table: '' };

// Today's reservations from the DB (reservationsList) — the basic-plan management list.
// The owner can add a walk-in/phone reservation and mark arrived/seated/no-show (real DB writes).
export default function InvitesPage() {
    const [rows, setRows] = useState([]);
    const [areas, setAreas] = useState([]);
    const [tables, setTables] = useState([]);        // raw table list (for the table picker)
    const [resRaw, setResRaw] = useState([]);        // normalized reservations (for free-table calc)
    const [seatDuration, setSeatDuration] = useState(90);   // from businessConfig
    const [upcomingLead, setUpcomingLead] = useState(30);   // the X: minutes before a reservation to flag it
    const [now, setNow] = useState(() => new Date());       // live clock for the "בעוד X" countdown
    // Per-table history modal.
    const [historyTable, setHistoryTable] = useState(null);
    const [historyRows, setHistoryRows] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const historySeqRef = useRef(0);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState('');
    const [multiSelect, setMultiSelect] = useState(false);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    // Waiting list (רשימת המתנה) — walk-ins waiting for a table.
    const [waiting, setWaiting] = useState([]);
    const [showWaitAdd, setShowWaitAdd] = useState(false);
    const [waitForm, setWaitForm] = useState({ guestName: '', guestPhone: '', numberOfGuests: '2' });
    const [waitSaving, setWaitSaving] = useState(false);
    // Unified reservation drawer (HOP-031) — holds the RAW reservation being edited.
    const [drawerRes, setDrawerRes] = useState(null);

    // Load the seating duration + upcoming-lead from business settings.
    useEffect(() => {
        let alive = true;
        businessConfigSvc.getConfig().then((c) => {
            if (!alive || !c) return;
            if (c.maxTimeTableIsInUseInMinutes) setSeatDuration(businessConfigSvc.effectiveHold(c));
            if (c.upcomingReservationLeadMinutes != null) setUpcomingLead(c.upcomingReservationLeadMinutes);
        });
        return () => { alive = false; };
    }, []);

    // Tick the clock so the "בעוד X" countdown stays current.
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 30000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        if (!toast) return undefined;
        const t = setTimeout(() => setToast(''), 3000);
        return () => clearTimeout(t);
    }, [toast]);

    const load = useCallback(async () => {
        try {
            const [{ rows: res }, areaList, tables, waitingRows] = await Promise.all([
                reservationsSvc.listToday([]),
                manageareas.getAllArea(true).catch(() => []),
                managetables.getAllTables(true).catch(() => []),
                reservationsSvc.waitingList().catch(() => []),
            ]);
            const areasArr = Array.isArray(areaList) ? areaList : [];
            const areaName = {};
            areasArr.forEach((a) => { areaName[String(a.id || a.ID)] = a.name; });
            const tableName = {};
            (Array.isArray(tables) ? tables : []).forEach((t) => { tableName[String(t.id)] = t.name; });
            const mapped = (res || []).map((r) => ({
                id: r.id,
                name: r.name || '—',
                size: r.party || '',
                area: areaName[String(r.area)] || (r.area != null && Number(r.area) ? `אזור ${r.area}` : ''),
                table: tableName[String(r.tableId)] || (r.tableId != null && Number(r.tableId) ? `שולחן ${r.tableId}` : '—'),
                time: r.time ? toHHMM(toMinutes(r.time)) : '',
                phone: r.phone || '',
                status: r.status || 'booked',
                joinGroupId: r.joinGroupId || 0,
            }));
            return { mapped, areasArr, tablesArr: Array.isArray(tables) ? tables : [], res: res || [], waitingArr: Array.isArray(waitingRows) ? waitingRows : [] };
        } catch (e) {
            return { mapped: [], areasArr: [], tablesArr: [], res: [], waitingArr: [] };
        }
    }, []);

    useEffect(() => {
        let alive = true;
        (async () => {
            const { mapped, areasArr, tablesArr, res, waitingArr } = await load();
            if (alive) { setRows(mapped); setAreas(areasArr); setTables(tablesArr); setResRaw(res); setWaiting(waitingArr); setLoading(false); }
        })();
        return () => { alive = false; };
    }, [load]);

    const refresh = useCallback(async () => {
        const { mapped, areasArr, tablesArr, res, waitingArr } = await load();
        setRows(mapped); setAreas(areasArr); setTables(tablesArr); setResRaw(res); setWaiting(waitingArr);
    }, [load]);

    // Refresh when a reservation is booked elsewhere (e.g. the global "הזמנת שולחן" navbar modal).
    useEffect(() => {
        const h = () => { refresh(); };
        window.addEventListener('hoppa:reservations-changed', h);
        return () => window.removeEventListener('hoppa:reservations-changed', h);
    }, [refresh]);

    // FREE tables for the reservation being added (its chosen time + party + area). Only free
    // tables are offered, honoring "רק מה שפנוי". Falls back to all tables if the time is blank.
    const freeTablesForAdd = useMemo(() => {
        if (!tables.length) return [];
        const atMinutes = form.startTime ? toMinutes(`${form.startTime}`) : toMinutes('19:00');
        const areaId = form.area ? Number(form.area) : null;
        return freeTablesFor(resRaw, tables, {
            atMinutes,
            party: Number(form.numberOfGuests) || 1,
            areaId,
            durationMin: seatDuration,
        });
    }, [tables, resRaw, form.startTime, form.numberOfGuests, form.area, seatDuration]);

    // If the chosen table drops out of the free list (operator changed time/party/area, or a
    // refresh occupied it), clear the selection so we never submit a stale/occupied table.
    useEffect(() => {
        // 'auto' is a valid non-table choice (server picks) — never clear it.
        if (form.table && form.table !== 'auto' && !freeTablesForAdd.some((t) => String(t.id) === String(form.table))) {
            setForm((f) => ({ ...f, table: '' }));
        }
    }, [freeTablesForAdd, form.table]);

    // Raw reservation by id + area names, for the per-row table picker.
    const resById = useMemo(() => {
        const m = new Map();
        resRaw.forEach((r) => m.set(String(r.id), r));
        return m;
    }, [resRaw]);
    const areaNameById = useMemo(() => {
        const m = new Map();
        areas.forEach((a) => m.set(Number(a.id || a.ID), a.name));
        return m;
    }, [areas]);

    const canSeatStatus = (s) => !['seated', 'cancelled', 'no-show'].includes(s);

    // Free tables RIGHT NOW — for seating a waiting guest. Depends on `now` so it recomputes
    // on the 30s clock tick (a table that frees up appears without a manual refresh).
    const freeTablesNow = useMemo(() => {
        if (!tables.length) return [];
        return freeTablesFor(resRaw, tables, { atMinutes: now.getHours() * 60 + now.getMinutes(), party: 1, areaId: null, durationMin: seatDuration });
    }, [tables, resRaw, seatDuration, now]);

    // Free tables for ONE existing reservation's time (excludes the row itself so its own
    // table shows as free). All areas, area shown as a label — assignment isn't area-locked.
    const freeTablesForRow = useCallback((raw) => {
        if (!raw || !tables.length) return [];
        return freeTablesFor(resRaw.filter((r) => String(r.id) !== String(raw.id)), tables, {
            atMinutes: raw.time ? toMinutes(raw.time) : toMinutes('19:00'),
            party: Number(raw.party) || 1,
            areaId: null,
            durationMin: seatDuration,
        });
    }, [resRaw, tables, seatDuration]);

    // Assign / change / clear a table for an existing reservation (seat=0 → keeps its status).
    const assignRowTable = useCallback(async (rowId, tableIdStr) => {
        const tableId = Number(tableIdStr) || 0;
        const res = await reservationsSvc.assignTable({ reservationId: rowId, tableId, seat: 0 });
        if (res && res.ok) { setToast(tableId ? 'השולחן שויך' : 'השולחן נוקה'); await refresh(); }
        else if (res && res.status === 409) { setToast('השולחן תפוס בשעה זו'); await refresh(); }
        else if (res && res.status === 418) { setToast('העסק מלא בשעה זו'); await refresh(); }
        else { setToast('לא ניתן לשייך שולחן'); }
    }, [refresh]);

    // "בעוד X דק'": minutes until a not-yet-seated reservation starts, if within the lead window.
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const minutesUntil = (raw) => {
        if (!raw || !canSeatStatus(raw.status) || !raw.time) return null;
        const diff = toMinutes(raw.time) - nowMinutes;
        return (diff > 0 && diff <= upcomingLead) ? diff : null;
    };

    // Per-table history modal (tableHistory act). Seq-guarded so a slower response can't land late.
    const closeHistory = useCallback(() => { historySeqRef.current += 1; setHistoryTable(null); }, []);
    const openTableHistory = useCallback(async (tableId, tableLabel) => {
        const seq = ++historySeqRef.current;
        setHistoryTable({ id: tableId, name: tableLabel });
        setHistoryLoading(true); setHistoryRows([]);
        try {
            const rowsH = await reservationsSvc.tableHistory(tableId);
            if (seq !== historySeqRef.current) return;
            setHistoryRows(Array.isArray(rowsH) ? rowsH : []);
        } catch (e) { if (seq === historySeqRef.current) setHistoryRows([]); }
        if (seq === historySeqRef.current) setHistoryLoading(false);
    }, []);

    // ── WAITING LIST handlers ──
    const nowClock = () => { const d = new Date(); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };
    const submitWaiting = async (e) => {
        e.preventDefault();
        if (!waitForm.guestName.trim() || waitSaving) return;
        setWaitSaving(true);
        const res = await reservationsSvc.create({
            date: todayISO(),
            startTime: `${nowClock()}:00`,
            numberOfGuests: Math.max(1, Number(waitForm.numberOfGuests) || 1),
            guestName: waitForm.guestName.trim(),
            guestPhone: waitForm.guestPhone.trim(),
            waiting: 1,
        });
        setWaitSaving(false);
        if (res && res.ok) { setToast('נוסף לרשימת ההמתנה'); setWaitForm({ guestName: '', guestPhone: '', numberOfGuests: '2' }); setShowWaitAdd(false); await refresh(); }
        else { setToast('לא ניתן להוסיף — נסו שוב'); }
    };
    // Seat a waiting guest at a chosen free table (seat=1 → moves them out of waiting, seated now).
    const seatWaiting = async (id, tableIdStr) => {
        const tableId = Number(tableIdStr) || 0;
        if (!tableId) return;
        const res = await reservationsSvc.assignTable({ reservationId: id, tableId, seat: 1 });
        if (res && res.ok) { setToast('הושב'); await refresh(); }
        else if (res && res.status === 409) { setToast('השולחן תפוס'); await refresh(); }
        else { setToast('לא ניתן להושיב — נסו שוב'); }
    };
    // Auto-seat a waiting guest — the server picks a best-fit free table right now.
    const seatWaitingAuto = async (id) => {
        const res = await reservationsSvc.assignTable({ reservationId: id, auto: 1, seat: 1 });
        if (res && res.ok) { setToast('הושב אוטומטית'); await refresh(); }
        else if (res && res.status === 410) { setToast('אין שולחן פנוי מתאים כרגע'); }
        else { setToast('לא ניתן להושיב — נסו שוב'); }
    };
    const removeWaiting = async (id) => {
        // Soft-delete (waitingRemove) — NOT cancel, so it doesn't leak into מוזמנים as בוטל or fire a notification.
        await reservationsSvc.waitingRemove(id);
        await refresh();
    };
    // Cancel a WHOLE linked group (big-group booking over several tables) at once.
    const cancelGroup = async (gid) => {
        if (!gid) return;
        await reservationsSvc.groupSetStatus(gid, 'cancelled');
        setToast('הקבוצה בוטלה');
        await refresh();
    };

    const toggleSelected = (id) => setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const exitMultiSelect = () => { setMultiSelect(false); setSelectedIds(new Set()); };

    // Drop selections that are no longer eligible after a refresh.
    useEffect(() => {
        setSelectedIds((prev) => {
            if (prev.size === 0) return prev;
            const valid = new Set(resRaw.filter((r) => canSeatStatus(r.status)).map((r) => String(r.id)));
            let changed = false;
            const next = new Set();
            prev.forEach((id) => { if (valid.has(String(id))) next.add(id); else changed = true; });
            return changed ? next : prev;
        });
    }, [resRaw]);

    // Mark all selected reservations "התיישבו" at once. Eligibility RE-CHECKED at call time.
    const bulkMarkSeated = async () => {
        const eligible = resRaw.filter((r) => selectedIds.has(String(r.id)) && canSeatStatus(r.status));
        const ids = eligible.map((r) => r.id);
        if (!ids.length) { exitMultiSelect(); setToast('אין הזמנות מתאימות לסימון'); return; }
        let failed = 0;
        for (const id of ids) {
            try { const res = await reservationsSvc.setStatus({ reservationId: id, status: 'seated' }); if (!res || !res.ok) failed += 1; }
            catch (e) { failed += 1; }
        }
        exitMultiSelect();
        setToast(failed ? `סומנו ${ids.length - failed}/${ids.length} כהתיישבו` : `סומנו ${ids.length} כהתיישבו`);
        await refresh();
    };

    const setStatus = useCallback(async (id, status) => {
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
        await reservationsSvc.setStatus({ reservationId: id, status });
        // Always resync (rows AND resRaw) so multi-select eligibility/pruning can't go stale.
        await refresh();
    }, [refresh]);

    const submitAdd = async (e) => {
        e.preventDefault();
        if (!form.guestName.trim() || saving) return;
        setSaving(true);
        const isAuto = form.table === 'auto';
        const res = await reservationsSvc.create({
            date: todayISO(),
            startTime: `${form.startTime}:00`,
            numberOfGuests: Math.max(1, Number(form.numberOfGuests) || 1),
            guestName: form.guestName.trim(),
            guestPhone: form.guestPhone.trim(),
            area: Number(form.area) || 0,
            ...(isAuto ? { autoTable: 1 } : { tableId: Number(form.table) || 0 }),
        });
        setSaving(false);
        if (res && res.ok) {
            setToast('ההזמנה נוספה'); setForm(EMPTY); setShowAdd(false); await refresh();
        } else if (res && res.status === 409) {
            setToast('השולחן כבר תפוס בשעה זו — בחרו שולחן אחר'); await refresh();
        } else if (res && res.status === 418) {
            setToast('העסק מלא בשעה זו (הגעתם לאחוז התפוסה)'); await refresh();
        } else {
            setToast('לא ניתן להוסיף את ההזמנה — נסו שוב');
        }
    };

    const tableField = { ...field, minHeight: 34, padding: '4px 8px', fontSize: 'var(--fs-xs)', minWidth: 120 };
    const upcomingBadge = { fontSize: '10px', fontWeight: 'var(--fw-bold)', color: 'var(--on-brand)', background: '#d9932a', borderRadius: 'var(--r-pill)', padding: '1px 7px', whiteSpace: 'nowrap' };
    const histBtn = { border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-muted)', borderRadius: 'var(--r-sm)', width: 30, minHeight: 30, cursor: 'pointer', fontSize: 13 };
    // Render "שעה" with a "בעוד X" badge for approaching reservations, and "שולחן" as a
    // FREE-table picker (+ a per-table history button).
    const groupBadge = { fontSize: '10px', fontWeight: 'var(--fw-bold)', color: 'var(--on-brand)', background: 'var(--brand-strong)', borderRadius: 'var(--r-pill)', padding: '1px 7px', whiteSpace: 'nowrap' };
    const baseColumns = RESERVATION_COLUMNS.map((c) => {
        if (c.id === 'name') {
            return {
                ...c,
                render: (row) => (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <button type="button" className="inv-name-btn"
                            onClick={() => { const raw = resById.get(String(row.id)); if (raw) setDrawerRes(raw); }}>
                            {row.name}
                        </button>
                        {row.joinGroupId > 0 && <span style={groupBadge} title="הזמנת קבוצה על כמה שולחנות">קבוצה</span>}
                    </span>
                ),
            };
        }
        if (c.id === 'time') {
            return {
                ...c,
                render: (row) => {
                    const raw = resById.get(String(row.id));
                    const mu = minutesUntil(raw);
                    return (
                        <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                            <span dir="ltr">{row.time}</span>
                            {mu != null && <span style={upcomingBadge}>בעוד {mu}׳</span>}
                        </span>
                    );
                },
            };
        }
        if (c.id !== 'table') return c;
        return {
            ...c,
            render: (row) => {
                const raw = resById.get(String(row.id));
                if (!raw) return row.table;
                // In multi-select the row is a selection target — show the table as plain text.
                if (multiSelect) return row.table;
                const free = freeTablesForRow(raw);
                const tbl = raw.tableId ? tables.find((t) => String(t.id) === String(raw.tableId)) : null;
                const tLabel = tbl ? tbl.name : raw.tableId;
                return (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <select style={tableField} value={raw.tableId ? String(raw.tableId) : ''}
                            onChange={(e) => assignRowTable(row.id, e.target.value)} aria-label="שולחן">
                            <option value="">ללא שולחן</option>
                            {free.map((t) => (
                                <option key={t.id} value={t.id}>
                                    שולחן {t.name}{areaNameById.get(t.areaId) ? ` · ${areaNameById.get(t.areaId)}` : ''}{t.fits ? '' : ' (קטן)'}
                                </option>
                            ))}
                        </select>
                        {raw.tableId > 0 && (
                            <button type="button" style={histBtn} title="היסטוריית שולחן"
                                onClick={() => openTableHistory(raw.tableId, tLabel)}>🕘</button>
                        )}
                    </span>
                );
            },
        };
    });
    const columns = [
        ...(multiSelect ? [{
            id: '_sel', text: '',
            render: (row) => {
                const raw = resById.get(String(row.id));
                if (!raw || !canSeatStatus(raw.status)) return null;
                return <input type="checkbox" style={{ width: 18, height: 18, accentColor: 'var(--brand)', cursor: 'pointer' }}
                    checked={selectedIds.has(String(row.id))} onChange={() => toggleSelected(String(row.id))} aria-label={`בחירת ${row.name}`} />;
            },
        }] : []),
        ...baseColumns,
        { id: 'status', text: 'סטטוס', render: (row) => <span style={badgeStyle(row.status)}>{(STATUS[row.status] || STATUS.booked).label}</span> },
        {
            id: 'actions', text: 'פעולות',
            render: (row) => (multiSelect ? null : (
                <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
                    <button type="button" style={actBtn} onClick={() => setStatus(row.id, 'arrived')}>הגיע</button>
                    <button type="button" style={actBtn} onClick={() => setStatus(row.id, 'seated')}>התיישב</button>
                    <button type="button" style={actBtn} onClick={() => setStatus(row.id, 'no-show')}>לא הגיע</button>
                    {row.joinGroupId > 0 && <button type="button" style={actBtn} onClick={() => cancelGroup(row.joinGroupId)}>בטל קבוצה</button>}
                </span>
            )),
        },
    ];

    return (
        <div style={{ direction: 'rtl', fontFamily: 'var(--font)' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 'var(--s-3)', alignItems: 'center', flexWrap: 'wrap', padding: 'var(--s-5) var(--s-5) 0' }}>
                <button type="button"
                    onClick={() => setShowAdd((v) => !v)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--brand)', color: 'var(--on-brand)', border: 'none', borderRadius: 'var(--r-pill)', padding: '10px 18px', fontWeight: 'var(--fw-bold)', fontFamily: 'var(--font)', cursor: 'pointer', minHeight: 44 }}>
                    <Icon name="plus" size={16} /> הזמנה חדשה
                </button>
                <button type="button"
                    onClick={() => (multiSelect ? exitMultiSelect() : setMultiSelect(true))}
                    style={{ background: multiSelect ? 'var(--brand)' : 'var(--bg-subtle)', color: multiSelect ? 'var(--on-brand)' : 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 'var(--r-pill)', padding: '10px 18px', fontWeight: 'var(--fw-medium)', fontFamily: 'var(--font)', cursor: 'pointer', minHeight: 44 }}>
                    {multiSelect ? 'ביטול בחירה' : 'בחירה מרובה'}
                </button>
                {multiSelect && selectedIds.size > 0 && (
                    <button type="button" onClick={bulkMarkSeated}
                        style={{ background: 'var(--success)', color: 'var(--on-brand)', border: 'none', borderRadius: 'var(--r-pill)', padding: '10px 18px', fontWeight: 'var(--fw-bold)', fontFamily: 'var(--font)', cursor: 'pointer', minHeight: 44 }}>
                        סמן שהתיישבו ({selectedIds.size})
                    </button>
                )}
            </div>

            {showAdd && (
                <form onSubmit={submitAdd} style={{ margin: 'var(--s-4) var(--s-5) 0', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', boxShadow: 'var(--shadow-sm)', padding: 'var(--s-5)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 'var(--s-3)', alignItems: 'end' }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
                        שם האורח
                        <input style={field} value={form.guestName} onChange={(e) => setForm({ ...form, guestName: e.target.value })} placeholder="שם מלא" />
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
                        טלפון
                        <input style={field} value={form.guestPhone} onChange={(e) => setForm({ ...form, guestPhone: e.target.value })} placeholder="050-0000000" dir="ltr" />
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
                        שעה
                        <input style={field} type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
                        מס' סועדים
                        <input style={field} type="number" min="1" max="30" value={form.numberOfGuests} onChange={(e) => setForm({ ...form, numberOfGuests: e.target.value })} />
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
                        אזור
                        <select style={field} value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value, table: '' })}>
                            <option value="">ללא אזור</option>
                            {areas.map((a) => <option key={a.id || a.ID} value={a.id || a.ID}>{a.name}</option>)}
                        </select>
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
                        שולחן (פנוי)
                        <select style={field} value={form.table} onChange={(e) => setForm({ ...form, table: e.target.value })}>
                            <option value="">ללא שולחן</option>
                            <option value="auto">אוטומטי — המערכת תבחר</option>
                            {freeTablesForAdd.map((t) => (
                                <option key={t.id} value={t.id}>שולחן {t.name} · {t.seats} מק'{t.fits ? '' : ' (קטן)'}</option>
                            ))}
                        </select>
                    </label>
                    <button type="submit" disabled={saving || !form.guestName.trim()}
                        style={{ background: 'var(--brand)', color: 'var(--on-brand)', border: 'none', borderRadius: 'var(--r-md)', padding: '0 20px', fontWeight: 'var(--fw-bold)', fontFamily: 'var(--font)', cursor: 'pointer', minHeight: 44, opacity: saving || !form.guestName.trim() ? 0.5 : 1 }}>
                        {saving ? 'שומר…' : 'הוספה'}
                    </button>
                </form>
            )}

            {/* ── רשימת המתנה (waiting list) ── */}
            <div style={{ margin: 'var(--s-4) var(--s-5) 0', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', boxShadow: 'var(--shadow-sm)', padding: 'var(--s-4)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--s-3)', flexWrap: 'wrap' }}>
                    <h2 style={{ margin: 0, fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-bold)' }}>
                        רשימת המתנה{waiting.length ? ` · ${waiting.length}` : ''}
                    </h2>
                    <button type="button" onClick={() => setShowWaitAdd((v) => !v)}
                        style={{ background: 'var(--bg-subtle)', color: 'var(--brand)', border: '1px solid var(--border)', borderRadius: 'var(--r-pill)', padding: '8px 16px', fontWeight: 'var(--fw-bold)', fontFamily: 'var(--font)', cursor: 'pointer', minHeight: 40 }}>
                        ＋ הוספה להמתנה
                    </button>
                </div>

                {showWaitAdd && (
                    <form onSubmit={submitWaiting} style={{ marginTop: 'var(--s-3)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 'var(--s-3)', alignItems: 'end' }}>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>שם
                            <input style={field} value={waitForm.guestName} onChange={(e) => setWaitForm({ ...waitForm, guestName: e.target.value })} placeholder="שם" autoFocus />
                        </label>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>טלפון
                            <input style={field} dir="ltr" value={waitForm.guestPhone} onChange={(e) => setWaitForm({ ...waitForm, guestPhone: e.target.value })} placeholder="050-0000000" />
                        </label>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>סועדים
                            <input style={field} type="number" min="1" max="60" value={waitForm.numberOfGuests} onChange={(e) => setWaitForm({ ...waitForm, numberOfGuests: e.target.value })} />
                        </label>
                        <button type="submit" disabled={waitSaving || !waitForm.guestName.trim()}
                            style={{ background: 'var(--brand)', color: 'var(--on-brand)', border: 'none', borderRadius: 'var(--r-md)', padding: '0 20px', fontWeight: 'var(--fw-bold)', fontFamily: 'var(--font)', cursor: 'pointer', minHeight: 44, opacity: waitSaving || !waitForm.guestName.trim() ? 0.5 : 1 }}>
                            {waitSaving ? 'שומר…' : 'הוסף'}
                        </button>
                    </form>
                )}

                <div style={{ marginTop: 'var(--s-3)', display: 'flex', flexDirection: 'column', gap: 'var(--s-2)' }}>
                    {waiting.length === 0 ? (
                        <div style={{ color: 'var(--text-faint)', fontSize: 'var(--fs-sm)', padding: 'var(--s-2)' }}>אין ממתינים כרגע</div>
                    ) : waiting.map((w) => {
                        const wm = w.time ? Math.max(0, nowMinutes - toMinutes(w.time)) : null;
                        return (
                            <div key={w.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)', flexWrap: 'wrap', padding: 'var(--s-2) var(--s-3)', background: 'var(--bg-subtle)', borderRadius: 'var(--r-md)' }}>
                                <span style={{ fontWeight: 'var(--fw-medium)', minWidth: 90 }}>{w.name || '—'}</span>
                                <span style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-xs)' }}>{w.party} סועדים</span>
                                {w.phone && <span dir="ltr" style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-xs)' }}>{w.phone}</span>}
                                {wm != null && <span style={{ color: 'var(--text-faint)', fontSize: 'var(--fs-xs)' }}>ממתין {wm}׳</span>}
                                <span style={{ marginInlineStart: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                                    <select defaultValue="" onChange={(e) => { const v = e.target.value; e.target.value = ''; seatWaiting(w.id, v); }} style={{ ...field, minHeight: 34, padding: '4px 8px', fontSize: 'var(--fs-xs)' }}>
                                        <option value="">הושב לשולחן…</option>
                                        {freeTablesNow.map((t) => (
                                            <option key={t.id} value={t.id}>שולחן {t.name} · {t.seats}{areaNameById.get(t.areaId) ? ` · ${areaNameById.get(t.areaId)}` : ''}</option>
                                        ))}
                                    </select>
                                    <button type="button" style={{ ...actBtn, background: 'var(--brand)', color: 'var(--on-brand)', border: 'none' }} onClick={() => seatWaitingAuto(w.id)}>אוטומטי</button>
                                    <button type="button" style={actBtn} onClick={() => removeWaiting(w.id)}>הסר</button>
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>

            <ListingPage
                title="מוזמנים"
                columns={columns}
                rows={rows}
                emptyText={loading ? 'טוען הזמנות…' : 'אין מוזמנים להיום'}
            />

            {toast && (
                <div style={{ position: 'fixed', insetBlockEnd: 'var(--s-6)', insetInlineStart: '50%', transform: 'translateX(50%)', background: 'var(--text)', color: 'var(--on-brand)', padding: '10px 20px', borderRadius: 'var(--r-pill)', boxShadow: 'var(--shadow-lg)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', zIndex: 60 }}>
                    {toast}
                </div>
            )}

            {historyTable && (
                <div onClick={closeHistory}
                    style={{ position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(20,20,30,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--s-4)' }}>
                    <div role="dialog" aria-label="היסטוריית שולחן" onClick={(e) => e.stopPropagation()}
                        style={{ width: 'min(460px, 100%)', maxHeight: 'min(76vh, 620px)', display: 'flex', flexDirection: 'column', background: 'var(--surface)', borderRadius: 'var(--r-lg)', boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--s-3) var(--s-4)', borderBottom: '1px solid var(--border)' }}>
                            <h3 style={{ margin: 0, fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-bold)' }}>היסטוריית שולחן {historyTable.name}</h3>
                            <button type="button" onClick={closeHistory} aria-label="סגור"
                                style={{ border: 'none', background: 'var(--bg-subtle)', borderRadius: 'var(--r-pill)', width: 32, height: 32, cursor: 'pointer', color: 'var(--text-muted)', fontSize: 15 }}>✕</button>
                        </div>
                        <div style={{ padding: 'var(--s-3)', overflowY: 'auto' }}>
                            {historyLoading ? (
                                <div style={{ padding: 'var(--s-6)', textAlign: 'center', color: 'var(--text-faint)', fontSize: 'var(--fs-sm)' }}>טוען…</div>
                            ) : historyRows.length === 0 ? (
                                <div style={{ padding: 'var(--s-6)', textAlign: 'center', color: 'var(--text-faint)', fontSize: 'var(--fs-sm)' }}>אין היסטוריה לשולחן זה</div>
                            ) : (
                                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--s-1)' }}>
                                    {historyRows.map((r, i) => (
                                        <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)', padding: 'var(--s-2) var(--s-3)', background: 'var(--bg-subtle)', borderRadius: 'var(--r-md)', fontSize: 'var(--fs-sm)' }}>
                                            <span dir="ltr" style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-xs)', minWidth: 74 }}>{String(r.date || '').slice(0, 10)}</span>
                                            <span dir="ltr" style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-xs)', minWidth: 38 }}>{String(r.time || '').slice(0, 5)}</span>
                                            <span style={{ flex: 1, fontWeight: 'var(--fw-medium)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name || '—'}</span>
                                            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-xs)' }}>{r.party} <Icon name="users" size={12} style={{ verticalAlign: 'middle' }} /></span>
                                            <span style={badgeStyle(r.status)}>{(STATUS[r.status] || STATUS.booked).label}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {drawerRes && (
                <ReservationDrawer
                    reservation={drawerRes}
                    areas={areas}
                    onClose={() => setDrawerRes(null)}
                    onSaved={refresh}
                />
            )}
        </div>
    );
}
